import React, { useState } from 'react';
import { View, ScrollView, Dimensions, Image } from 'react-native';
import styled from 'styled-components/native';
import { useTranslation } from 'react-i18next';
import { useNavigation } from '@react-navigation/native';
import { Text, Button, Chip, Card } from '@ui';
import { useOnboardingStore } from '@stores/onboardingStore';
import { mockJourneys } from '@mocks/journeys';

const { width } = Dimensions.get('window');

const Container = styled.View`
  flex: 1;
  background-color: ${({ theme }) => theme.colors.background};
`;

const PageContainer = styled.View`
  flex: 1;
  padding: ${({ theme }) => theme.spacing.xl}px;
  justify-content: space-between;
`;

const ContentArea = styled.View`
  flex: 1;
  justify-content: center;
  align-items: center;
`;

const ButtonArea = styled.View`
  padding-bottom: ${({ theme }) => theme.spacing.xl}px;
`;

const DotsContainer = styled.View`
  flex-direction: row;
  justify-content: center;
  align-items: center;
  margin-bottom: ${({ theme }) => theme.spacing.xl}px;
`;

const Dot = styled.View<{ active: boolean }>`
  width: 8px;
  height: 8px;
  border-radius: 4px;
  background-color: ${({ theme, active }) =>
    active ? theme.colors.brandPrimary : theme.colors.neutral300};
  margin-horizontal: ${({ theme }) => theme.spacing.xs}px;
`;

const MomentsGrid = styled.View`
  flex-wrap: wrap;
  flex-direction: row;
  justify-content: center;
  gap: ${({ theme }) => theme.spacing.md}px;
  margin-top: ${({ theme }) => theme.spacing.xl}px;
  width: 100%;
`;

const IllustrationImage = styled(Image)`
  width: 280px;
  height: 280px;
  margin-bottom: ${({ theme }) => theme.spacing.xl}px;
`;

const OnboardingScreen: React.FC = () => {
  const { t } = useTranslation();
  const navigation = useNavigation();
  const { currentStep, setCurrentStep, selectedMoments, toggleMoment, setCompleted } =
    useOnboardingStore();

  const handleNext = () => {
    if (currentStep < 3) {
      setCurrentStep(currentStep + 1);
    } else {
      setCompleted(true);
    }
  };

  const handleSkip = () => {
    setCompleted(true);
  };

  const renderWelcome = () => (
    <PageContainer>
      <ContentArea>
        <IllustrationImage
          source={{
            uri: 'https://images.unsplash.com/photo-1576091160399-112ba8d25d1d?w=800&q=80',
          }}
          resizeMode="contain"
        />
        <Text variant="display" color="textPrimary" align="center" style={{ marginBottom: 16 }}>
          {t('onboarding.welcome.title')}
        </Text>
        <Text variant="body" color="textSecondary" align="center" style={{ marginTop: 8 }}>
          {t('onboarding.welcome.subtitle')}
        </Text>
      </ContentArea>
      <ButtonArea>
        <Button variant="primary" fullWidth onPress={handleNext}>
          {t('onboarding.welcome.getStarted')}
        </Button>
      </ButtonArea>
    </PageContainer>
  );

  const renderPermissions = () => (
    <PageContainer>
      <ContentArea>
        <Text variant="headline" color="textPrimary" align="center" style={{ marginBottom: 16 }}>
          {t('onboarding.permissions.title')}
        </Text>
        <Text variant="body" color="textSecondary" align="center" style={{ marginTop: 8 }}>
          {t('onboarding.permissions.subtitle')}
        </Text>
      </ContentArea>
      <ButtonArea>
        <Button variant="primary" fullWidth onPress={handleNext} style={{ marginBottom: 12 }}>
          {t('onboarding.permissions.allow')}
        </Button>
        <Button variant="ghost" fullWidth onPress={handleNext}>
          {t('common.skip')}
        </Button>
      </ButtonArea>
    </PageContainer>
  );

  const renderMoments = () => (
    <PageContainer>
      <View style={{ flex: 1, marginTop: 48 }}>
        <Text variant="headline" color="textPrimary" align="center" style={{ marginBottom: 8 }}>
          {t('onboarding.moments.title')}
        </Text>
        <Text variant="body" color="textSecondary" align="center" style={{ marginBottom: 16 }}>
          {t('onboarding.moments.subtitle')}
        </Text>
        <ScrollView showsVerticalScrollIndicator={false}>
          <MomentsGrid>
            {mockJourneys.map(journey => (
              <Chip
                key={journey.id}
                variant="tonal"
                selected={selectedMoments.includes(journey.id)}
                onPress={() => toggleMoment(journey.id)}
                icon={journey.icon}
              >
                {journey.title}
              </Chip>
            ))}
          </MomentsGrid>
        </ScrollView>
      </View>
      <ButtonArea>
        <Text variant="caption" color="textSecondary" align="center" style={{ marginBottom: 12 }}>
          {selectedMoments.length} {t('onboarding.moments.selected')}
        </Text>
        <Button
          variant="primary"
          fullWidth
          onPress={handleNext}
          disabled={selectedMoments.length === 0}
        >
          {t('common.continue')}
        </Button>
      </ButtonArea>
    </PageContainer>
  );

  const renderSignIn = () => (
    <PageContainer>
      <ContentArea>
        <Text variant="headline" color="textPrimary" align="center" style={{ marginBottom: 16 }}>
          {t('onboarding.signIn.title')}
        </Text>
        <Text variant="body" color="textSecondary" align="center" style={{ marginBottom: 24 }}>
          {t('onboarding.signIn.subtitle')}
        </Text>
      </ContentArea>
      <ButtonArea>
        <Button variant="primary" fullWidth onPress={handleSkip} style={{ marginBottom: 12 }}>
          {t('onboarding.signIn.demo')}
        </Button>
      </ButtonArea>
    </PageContainer>
  );

  const pages = [renderWelcome, renderPermissions, renderMoments, renderSignIn];

  return (
    <Container>
      {pages[currentStep]()}
      <DotsContainer>
        {pages.map((_, index) => (
          <Dot key={index} active={index === currentStep} />
        ))}
      </DotsContainer>
    </Container>
  );
};

export default OnboardingScreen;
